import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { TideChart } from "./TideChart";
import { GoldenGateTide } from "./GoldenGateTide";
import "./index.css";

interface Station {
	id: string;
	name: string;
	state: string;
	stationId: string;
}

interface DataPoint {
	t: string;
	v: string;
}

interface HiLoPoint extends DataPoint {
	type: string;
}

interface TideData {
	station: Station;
	waterLevels: DataPoint[];
	predictions: DataPoint[];
	hiLo: HiLoPoint[];
}

function getCurrentLevel(waterLevels: DataPoint[]): DataPoint | null {
	if (waterLevels.length === 0) return null;
	return waterLevels[waterLevels.length - 1];
}

function getTideStatus(hiLo: HiLoPoint[]): { status: string; next: HiLoPoint | null } {
	const now = Date.now();
	const upcoming = hiLo
		.filter((p) => new Date(p.t).getTime() > now)
		.sort((a, b) => new Date(a.t).getTime() - new Date(b.t).getTime());

	if (upcoming.length === 0) return { status: "Unknown", next: null };

	const next = upcoming[0];
	if (next.type === "H") {
		return { status: "Rising", next };
	} else {
		return { status: "Falling", next };
	}
}

function formatTime(t: string): string {
	return new Date(t).toLocaleTimeString("en-US", {
		hour: "numeric",
		minute: "2-digit",
	});
}

export function App() {
	const [stations, setStations] = useState<Station[]>([]);
	const [selectedCity, setSelectedCity] = useState("san-francisco");
	const [tideData, setTideData] = useState<TideData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [hoverData, setHoverData] = useState<{ level: number; time: number } | null>(null);

	const handleChartHover = useCallback((data: { level: number; time: number } | null) => {
		setHoverData(data);
	}, []);

	const [darkMode, setDarkMode] = useState(() => {
		const saved = localStorage.getItem("theme");
		if (saved) return saved === "dark";
		return window.matchMedia("(prefers-color-scheme: dark)").matches;
	});

	// Apply dark class to <html> and persist preference
	useEffect(() => {
		document.documentElement.classList.toggle("dark", darkMode);
		localStorage.setItem("theme", darkMode ? "dark" : "light");
	}, [darkMode]);

	// Listen for system theme changes (only when no saved preference)
	useEffect(() => {
		const mq = window.matchMedia("(prefers-color-scheme: dark)");
		const handler = (e: MediaQueryListEvent) => {
			if (!localStorage.getItem("theme")) {
				setDarkMode(e.matches);
			}
		};
		mq.addEventListener("change", handler);
		return () => mq.removeEventListener("change", handler);
	}, []);

	useEffect(() => {
		fetch("/api/stations")
			.then((r) => r.json())
			.then((data) => setStations(data))
			.catch(() => {});
	}, []);

	useEffect(() => {
		setLoading(true);
		setError(null);
		fetch(`/api/tides/${selectedCity}`)
			.then((r) => {
				if (!r.ok) throw new Error("Failed to fetch");
				return r.json();
			})
			.then((data) => {
				setTideData(data);
				setLoading(false);
			})
			.catch(() => {
				setError("Failed to load tide data. Please try again.");
				setLoading(false);
			});
	}, [selectedCity]);

	const currentLevel = tideData ? getCurrentLevel(tideData.waterLevels) : null;
	const tideStatus = tideData ? getTideStatus(tideData.hiLo) : null;

	// Get upcoming high/low tides
	const now = Date.now();
	const upcomingTides = tideData
		? tideData.hiLo
				.filter((p) => new Date(p.t).getTime() > now)
				.sort((a, b) => new Date(a.t).getTime() - new Date(b.t).getTime())
				.slice(0, 6)
		: [];

	return (
		<div className="w-full max-w-4xl mx-auto p-4 md:p-6 space-y-4">
			{/* Header */}
			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
				<div>
					<h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
						<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500">
							<path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
							<path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
							<path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
						</svg>
						Tide Tracker
					</h1>
					<p className="text-muted-foreground text-sm">
						Real-time tide levels and forecast from NOAA
					</p>
				</div>
				<div className="flex items-center gap-2">
					<Select value={selectedCity} onValueChange={setSelectedCity}>
						<SelectTrigger className="w-full sm:w-[220px]">
							<SelectValue placeholder="Select a city" />
						</SelectTrigger>
						<SelectContent>
							{stations.map((s) => (
								<SelectItem key={s.id} value={s.id}>
									{s.name}, {s.state}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<button
						onClick={() => setDarkMode((d) => !d)}
						className="inline-flex items-center justify-center rounded-md border border-input bg-background h-9 w-9 hover:bg-accent hover:text-accent-foreground transition-colors"
						aria-label="Toggle dark mode"
					>
						{darkMode ? (
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
								<circle cx="12" cy="12" r="4" />
								<path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
							</svg>
						) : (
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
								<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
							</svg>
						)}
					</button>
				</div>
			</div>

			{/* Loading / Error */}
			{loading && (
				<div className="flex items-center justify-center py-20">
					<div className="flex flex-col items-center gap-3">
						<div className="animate-spin rounded-full h-8 w-8 border-2 border-muted border-t-primary" />
						<p className="text-sm text-muted-foreground">Loading tide data...</p>
					</div>
				</div>
			)}

			{error && (
				<Card className="border-destructive/50 bg-destructive/5">
					<CardContent className="pt-6">
						<p className="text-destructive text-sm">{error}</p>
					</CardContent>
				</Card>
			)}

			{!loading && !error && tideData && (
				<>
					{/* Current conditions cards */}
					<div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
						<Card>
							<CardContent className="pt-5 pb-4">
								<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
									Current Level
								</p>
								<p className="text-3xl font-bold mt-1 tabular-nums">
									{currentLevel ? `${parseFloat(currentLevel.v).toFixed(2)} ft` : "--"}
								</p>
								<p className="text-xs text-muted-foreground mt-1">
									Above MLLW datum
								</p>
							</CardContent>
						</Card>

						<Card>
							<CardContent className="pt-5 pb-4">
								<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
									Tide Status
								</p>
								<div className="flex items-center gap-2 mt-1">
									<span className="text-3xl font-bold">
										{tideStatus?.status || "--"}
									</span>
									{tideStatus?.status === "Rising" && (
										<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-emerald-500">
											<path d="M12 19V5M5 12l7-7 7 7" />
										</svg>
									)}
									{tideStatus?.status === "Falling" && (
										<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-orange-500">
											<path d="M12 5v14M5 12l7 7 7-7" />
										</svg>
									)}
								</div>
								<p className="text-xs text-muted-foreground mt-1">
									{tideStatus?.next
										? `Next ${tideStatus.next.type === "H" ? "high" : "low"} at ${formatTime(tideStatus.next.t)}`
										: ""}
								</p>
							</CardContent>
						</Card>

						<Card>
							<CardContent className="pt-5 pb-4">
								<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
									Station
								</p>
								<p className="text-lg font-semibold mt-1">
									{tideData.station.name}
								</p>
								<p className="text-xs text-muted-foreground mt-1">
									NOAA #{tideData.station.stationId}
								</p>
							</CardContent>
						</Card>
					</div>

					{/* Golden Gate Bridge 3D Visualization (SF only) */}
					{selectedCity === "san-francisco" && currentLevel && (
						<Card>
							<CardHeader className="pb-2">
								<CardTitle className="text-base">Golden Gate Bridge — Live Tide</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="h-[260px] md:h-[320px]">
									<GoldenGateTide
										currentLevel={hoverData?.level ?? parseFloat(currentLevel.v)}
										tideStatus={hoverData ? new Date(hoverData.time).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : (tideStatus?.status || "")}
										darkMode={darkMode}
										hoverTime={hoverData?.time ?? null}
									/>
								</div>
							</CardContent>
						</Card>
					)}

					{/* Tide Chart */}
					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-base">Tide Chart</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="h-[300px] md:h-[350px]">
								<TideChart
									waterLevels={tideData.waterLevels}
									predictions={tideData.predictions}
									hiLo={tideData.hiLo}
									darkMode={darkMode}
									onHover={selectedCity === "san-francisco" ? handleChartHover : undefined}
								/>
							</div>
						</CardContent>
					</Card>

					{/* Upcoming Tides */}
					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-base">Upcoming Tides</CardTitle>
						</CardHeader>
						<CardContent>
							{upcomingTides.length > 0 ? (
								<div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
									{upcomingTides.map((tide, i) => {
										const isHigh = tide.type === "H";
										const d = new Date(tide.t);
										return (
											<div
												key={i}
												className={`rounded-lg border px-4 py-3 ${
													isHigh
														? "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800"
														: "bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-800"
												}`}
											>
												<div className="flex items-center gap-1.5">
													<span
														className={`text-xs font-bold uppercase ${
															isHigh ? "text-blue-600 dark:text-blue-400" : "text-orange-600 dark:text-orange-400"
														}`}
													>
														{isHigh ? "High" : "Low"}
													</span>
												</div>
												<p className="text-xl font-bold tabular-nums mt-0.5">
													{parseFloat(tide.v).toFixed(2)} ft
												</p>
												<p className="text-xs text-muted-foreground mt-0.5">
													{d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
													{" at "}
													{d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
												</p>
											</div>
										);
									})}
								</div>
							) : (
								<p className="text-sm text-muted-foreground">No upcoming tide data available.</p>
							)}
						</CardContent>
					</Card>
				</>
			)}
		</div>
	);
}

export default App;
